import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import RecalcPreviewModal, { RecalcWeeksPreview } from '../modal/RecalcPreviewModal';
import CalendarPicker from '../util/CalendarPicker';

interface Segment {
  id: number;
  patternId: number;
  patternName: string | null;
  hoursPerWeek: number | null;
  start: string;        // 'YYYY-MM-DD'
  end: string | null;   // null = открытый (текущий)
  isCurrent: boolean;
  isFirst: boolean;
  reason: string | null;
  notes: string | null;
}
interface TimelineData {
  segments: Segment[];
  contractStartDate: string | null;
  anchorDate: string | null;
  hasContractDate: boolean;
  timeZone: string | null;
  patterns: { id: number; name: string; hoursPerWeek: number }[];
}

interface Props {
  userId: number;
  canEdit: boolean;
  onChanged?: () => void; // что-то изменилось → родителю обновить карточку
}

const fmt = (s: string | null) => {
  if (!s) return '—';
  try { return format(new Date(`${s}T00:00:00`), 'dd MMM yyyy'); } catch { return s; }
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const HrPatternTimeline: React.FC<Props> = ({ userId, canEdit, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<TimelineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editId, setEditId] = useState<number | null>(null);
  const [editPattern, setEditPattern] = useState('');
  const [editDate, setEditDate] = useState('');

  const [adding, setAdding] = useState(false);
  const [addPattern, setAddPattern] = useState('');
  const [addDate, setAddDate] = useState('');
  const [addReason, setAddReason] = useState('');

  const [contractDate, setContractDate] = useState('');
  const [savingContract, setSavingContract] = useState(false);

  // Модалка проверки/перебалансировки по неделям
  const [recalcPreview, setRecalcPreview] = useState<RecalcWeeksPreview | null>(null);
  const [recalcTrigger, setRecalcTrigger] = useState<'manual' | 'on_change'>('manual');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`hr-pattern-history/timeline/${userId}`);
      setData(data);
      setContractDate(data.contractStartDate || '');
    } catch { /* глобальный interceptor */ } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const err = (e: any) => {
    const code = e?.response?.data?.message;
    const map: Record<string, string> = {
      effectiveDate_after_next: t('HrErrAfterNext', 'The date must be before the next pattern change.') as string,
      effectiveDate_before_previous: t('HrErrBeforePrev', 'The date must be after the previous pattern change.') as string,
    };
    enqueueSnackbar((code && map[code]) || code || (t('Error') as string), { variant: 'error' });
  };

  // Проверка (read-only): если есть расхождения — предупреждение с деталями; иначе просто тост.
  const runCheck = async (trigger: 'manual' | 'on_change', okMsg: string) => {
    try {
      const { data } = await axios.post(`hr-recalc/preview-weeks/${userId}`);
      const total = (data.summary?.weeks ?? 0) + (data.summary?.leave ?? 0);
      if (total === 0) { enqueueSnackbar(okMsg, { variant: 'success' }); }
      else { setRecalcTrigger(trigger); setRecalcPreview(data); }
    } catch { /* interceptor */ }
  };

  const afterMutation = async (okMsg: string) => {
    await load();
    onChanged?.();
    await runCheck('on_change', okMsg);
  };

  const startEdit = (s: Segment) => { setEditId(s.id); setEditPattern(String(s.patternId)); setEditDate(s.start); };
  const cancelEdit = () => { setEditId(null); setEditPattern(''); setEditDate(''); };

  const saveEdit = async (s: Segment) => {
    if (!editPattern) { enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const body: any = { newPatternId: Number(editPattern) !== s.patternId ? Number(editPattern) : undefined };
      // Дату можно менять только у не-якорных сегментов
      if (!s.isFirst && editDate && editDate !== s.start) body.effectiveDate = editDate;
      await axios.put(`hr-pattern-history/segment/${s.id}`, body);
      cancelEdit();
      await afterMutation(t('Saved') as string);
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const del = async (s: Segment) => {
    setSaving(true);
    try {
      await axios.delete(`hr-pattern-history/segment/${s.id}`);
      cancelEdit();
      await afterMutation(t('Saved') as string);
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const add = async () => {
    if (!addPattern || !addDate) { enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      await axios.post('hr-pattern-history/change', {
        userId, newPatternId: Number(addPattern), effectiveDate: addDate, reason: addReason || undefined,
      });
      setAdding(false); setAddPattern(''); setAddDate(''); setAddReason('');
      await afterMutation(t('HrPatternSaved') || 'Pattern added');
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  // Дата поступления: пишем сотруднику + двигаем якорный сегмент
  const saveContract = async () => {
    if (!contractDate) { enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return; }
    if (contractDate === data?.contractStartDate) return;
    const anchor = data?.segments?.[0];
    const next = data?.segments?.[1];
    // Дата контракта = начало первого (якорного) сегмента; не может быть на/после следующей смены паттерна.
    // Предпроверка на фронте: не сохраняем НИЧЕГО (ни дату, ни паттерн), сразу понятная подсказка.
    if (next && contractDate >= next.start) {
      enqueueSnackbar(
        (t('HrContractAfterSegment', 'Contract start must be before the next pattern change ({date}). Move or remove that change first.') as string)
          .replace('{date}', fmt(next.start)),
        { variant: 'warning' },
      );
      return;
    }
    setSavingContract(true);
    try {
      // Сначала двигаем якорный сегмент (валидация на бэке), и только если ок — сохраняем дату контракта.
      if (anchor && anchor.start !== contractDate) {
        await axios.put(`hr-pattern-history/segment/${anchor.id}`, { effectiveDate: contractDate });
      }
      await axios.put(`update-user/${userId}`, { contract_start_date: contractDate });
      await afterMutation(t('Saved') as string);
    } catch (e) { err(e); } finally { setSavingContract(false); }
  };

  if (loading) return <div style={{ padding: 16, fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{t('Loading') || 'Loading…'}</div>;
  if (!data) return null;

  const patternOpts = data.patterns;
  const contractChanged = !!contractDate && contractDate !== (data.contractStartDate || '');
  const today = todayStr();
  const currentSeg = data.segments.find(s => s.isCurrent) || data.segments[data.segments.length - 1];
  const tz = data.timeZone || t('LocalTime') || 'local time';

  const lblSt: React.CSSProperties = { fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-color-calendar-week)' };

  return (
    <div>
      {/* Верхний блок: дата старта + текущий паттерн (read-only) */}
      <div className="ts-card" style={{ padding: '12px 14px', marginBottom: 12, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={lblSt}>{t('ContractStartDate') || 'Contract start date'}</span>
          <CalendarPicker mode="single" variant="field" output="string"
            value={contractDate} disabled={!canEdit} onChange={(v) => setContractDate(v)} />
        </div>
        <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={lblSt}>{t('HrCurrentPattern') || 'Current pattern'}</span>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-color)' }}>
            {currentSeg?.patternName || '—'}
            {currentSeg?.hoursPerWeek != null && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar-week)' }}> · {currentSeg.hoursPerWeek}{t('HShort') || 'h'}/{t('HrPerWeek') || 'week'}</span>}
          </div>
          <span style={{ fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>{t('HrPatternFromContract') || 'Applies from contract start · edit patterns below'}</span>
        </div>
        {canEdit && contractChanged && (
          <button type="button" className="btn-primary" style={{ fontSize: 12 }} disabled={savingContract} onClick={saveContract}>{t('Save')}</button>
        )}
      </div>

      {!data.hasContractDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, marginBottom: 10,
          background: 'var(--bg-alert, rgba(245,158,11,.12))', color: 'var(--text-alert, #b45309)', fontSize: 12, fontWeight: 600 }}>
          ⚠️ {t('HrNoContractDate') || 'Contract start date is not set — the first segment uses the account creation date. Please fill it in.'}
        </div>
      )}

      {/* Список сегментов */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.segments.map((s) => {
          const isFuture = s.start > today;
          return (
            <div key={s.id} className="ts-card" style={{ padding: '10px 12px' }}>
              {editId === s.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <label style={lblSt}>{t('HrWorkingPattern') || 'Pattern'}</label>
                      <select title={t('HrWorkingPattern') as string} className="timesheet-edit-input" value={editPattern} onChange={e => setEditPattern(e.target.value)}>
                        {patternOpts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.hoursPerWeek}h)</option>)}
                      </select>
                    </div>
                    {s.isFirst ? (
                      <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={lblSt}>{t('HrSegmentFrom') || 'From'}</label>
                        <div className="timesheet-edit-input" style={{ color: 'var(--text-color-calendar-week)', background: 'var(--bg-disable)' }}>{fmt(s.start)}</div>
                      </div>
                    ) : (
                      <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <label style={lblSt}>{t('HrSegmentFrom') || 'From'}</label>
                        <CalendarPicker mode="single" variant="field" output="string" value={editDate} onChange={(v) => setEditDate(v)} />
                      </div>
                    )}
                  </div>
                  {s.isFirst && <div style={{ fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>{t('HrDateFromContract') || 'Date is taken from contract start (not editable)'}</div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    {!s.isFirst
                      ? <button type="button" className="dt-btn-ghost" style={{ fontSize: 12, color: 'var(--err,#ef4444)', borderColor: 'rgba(239,68,68,.4)' }} disabled={saving} onClick={() => del(s)}>🗑 {t('Delete')}</button>
                      : <span />}
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} onClick={cancelEdit}>{t('Cancel')}</button>
                      <button type="button" className="btn-primary" style={{ fontSize: 12 }} disabled={saving} onClick={() => saveEdit(s)}>{t('Save')}</button>
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-color)' }}>{s.patternName || '—'}</span>
                      {s.hoursPerWeek != null && <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{s.hoursPerWeek}{t('HShort') || 'h'}/{t('HrPerWeek') || 'week'}</span>}
                      {s.isCurrent && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: 'rgba(5,150,105,.14)', color: '#059669' }}>{t('Current') || 'Current'}</span>}
                      {isFuture && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: 'rgba(37,99,235,.12)', color: '#2563eb' }}>{t('HrScheduledChange') || 'Scheduled'}</span>}
                      {s.isFirst && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: 'var(--surface2)', color: 'var(--text-color-calendar-week)' }}>{t('Initial') || 'From contract'}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-color-calendar)', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(s.start)} — {s.end ? fmt(s.end) : (t('HrOngoing') || 'ongoing')}
                    </div>
                    {isFuture && (
                      <div style={{ fontSize: 11, color: 'var(--warn,#b45309)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                        ⏳ {(t('HrAutoApplyHint') || 'Applies automatically on {date} at 00:00 ({tz})').replace('{date}', fmt(s.start)).replace('{tz}', String(tz))}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <button type="button" className="btn-icon" title={t('Edit') as string} onClick={() => startEdit(s)}>✏️</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Форма добавления */}
      {canEdit && adding && (
        <div className="ts-card" style={{ padding: '10px 12px', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={lblSt}>{t('HrWorkingPattern') || 'Pattern'}</label>
              <select title={t('HrWorkingPattern') as string} className="timesheet-edit-input" value={addPattern} onChange={e => setAddPattern(e.target.value)}>
                <option value="">—</option>
                {patternOpts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.hoursPerWeek}h)</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 130px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <label style={lblSt}>{t('HrSegmentFrom') || 'Effective from'}</label>
              <CalendarPicker mode="single" variant="field" output="string" value={addDate} onChange={(v) => setAddDate(v)} />
            </div>
          </div>
          <input className="timesheet-edit-input" placeholder={t('ReasonForUpdate') as string} value={addReason} onChange={e => setAddReason(e.target.value)} />
          <div style={{ fontSize: 10.5, color: 'var(--text-color-calendar-week)', fontStyle: 'italic' }}>
            {t('HrAddHint') || 'On save a check runs. If TOIL/leave are affected, you will be asked to confirm.'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} onClick={() => setAdding(false)}>{t('Cancel')}</button>
            <button type="button" className="btn-primary" style={{ fontSize: 12 }} disabled={saving} onClick={add}>{t('HrAddSegment') || 'Add change'}</button>
          </div>
        </div>
      )}

      {/* Футер: Recalculate (слева) + Add pattern change (справа) */}
      {canEdit && !adding && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
          <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} onClick={() => runCheck('manual', t('HrRecalcNoChanges') || 'No changes')}>
            🔁 {t('HrRecalculate') || 'Recalculate'}
          </button>
          <button type="button" className="dt-btn-ghost" style={{ fontSize: 12 }} onClick={() => { setAdding(true); setAddDate(todayStr()); }}>
            + {t('HrAddSegment') || 'Add pattern change'}
          </button>
        </div>
      )}

      {recalcPreview && (
        <RecalcPreviewModal
          userId={userId}
          preview={recalcPreview}
          trigger={recalcTrigger}
          onClose={() => setRecalcPreview(null)}
          onChanged={() => { load(); onChanged?.(); }}
        />
      )}
    </div>
  );
};

export default HrPatternTimeline;
